import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CategoryBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const EMPTY = { name: "", polarity: "positif" };

export default function Pengaturan() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, polarity: c.polarity });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Kategori diperbarui");
      } else {
        await api.post("/categories", form);
        toast.success("Kategori ditambahkan");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/categories/${deleteTarget.id}`);
      toast.success("Kategori dihapus");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-10">
        <div>
          <div className="label-overline">Pengaturan</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-2">
            Kategori
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-lg">
            Kelola kategori objek Mad’u secara kustom. Kategori <span className="font-semibold text-emerald-700">positif</span> akan berwarna hijau, <span className="font-semibold text-red-700">negatif</span> berwarna merah.
          </p>
        </div>
        <Button
          data-testid="add-category-btn"
          className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
          onClick={openCreate}
        >
          <Plus size={14} className="mr-2" /> Tambah Kategori
        </Button>
      </div>

      {loading ? (
        <div className="label-overline text-center py-16">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="border border-neutral-200 p-12 text-center">
          <div className="font-display font-bold text-xl mb-2">Belum ada kategori</div>
          <div className="text-sm text-neutral-500">Tambah kategori pertama untuk mulai mengelompokkan objek Mad’u.</div>
        </div>
      ) : (
        <div className="border border-neutral-200" data-testid="category-list">
          <div className="hidden md:grid grid-cols-[1fr_180px_160px_100px] border-b border-neutral-200">
            <div className="label-overline p-4">Nama Kategori</div>
            <div className="label-overline p-4">Polaritas</div>
            <div className="label-overline p-4">Preview</div>
            <div className="label-overline p-4 text-right">Aksi</div>
          </div>
          <div className="divide-y divide-neutral-100">
            {items.map((c) => (
              <div
                key={c.id}
                className="md:grid md:grid-cols-[1fr_180px_160px_100px] p-4 md:p-0"
                data-testid={`category-row-${c.id}`}
              >
                <div className="md:p-4 font-semibold">{c.name}</div>
                <div className="md:p-4 text-sm mt-1 md:mt-0">
                  <span
                    className={`inline-block px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider border ${
                      c.polarity === "positif"
                        ? "border-emerald-600 text-emerald-700"
                        : "border-red-600 text-red-700"
                    }`}
                    data-testid={`category-polarity-${c.id}`}
                  >
                    {c.polarity}
                  </span>
                </div>
                <div className="md:p-4 mt-2 md:mt-0">
                  <CategoryBadge name={c.name} polarity={c.polarity} testid={`category-preview-${c.id}`} />
                </div>
                <div className="md:p-4 mt-3 md:mt-0 flex md:justify-end gap-1">
                  <button
                    data-testid={`edit-category-${c.id}`}
                    onClick={() => openEdit(c)}
                    className="p-2 border border-neutral-300 md:border-transparent hover:bg-black hover:text-white transition-colors duration-150"
                    aria-label="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`delete-category-${c.id}`}
                    onClick={() => setDeleteTarget(c)}
                    className="p-2 border border-neutral-300 md:border-transparent hover:bg-black hover:text-white transition-colors duration-150"
                    aria-label="Hapus"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-black max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">
              {editing ? "Edit Kategori" : "Tambah Kategori"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-overline">Nama Kategori *</Label>
              <Input
                required
                data-testid="input-category-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-none border-black h-11"
                placeholder="mis. Netral, Simpatisan, Oposan"
              />
            </div>
            <div>
              <Label className="label-overline">Polaritas *</Label>
              <Select value={form.polarity} onValueChange={(v) => setForm({ ...form, polarity: v })}>
                <SelectTrigger data-testid="input-category-polarity" className="mt-1 rounded-none border-black h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="positif">Positif (hijau)</SelectItem>
                  <SelectItem value="negatif">Negatif (merah)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.name && (
              <div>
                <Label className="label-overline">Preview</Label>
                <div className="mt-2">
                  <CategoryBadge name={form.name} polarity={form.polarity} testid="category-preview-form" />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="category-cancel"
                className="rounded-none border-black"
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="category-save"
                className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
              >
                {saving ? "MENYIMPAN..." : "SIMPAN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-black">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black tracking-tight">Hapus Kategori?</AlertDialogTitle>
            <AlertDialogDescription>
              Menghapus <strong>{deleteTarget?.name}</strong> hanya bisa dilakukan jika belum ada objek Mad’u yang menggunakannya.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-category-cancel" className="rounded-none border-black">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-category-confirm"
              onClick={confirmDelete}
              className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
            >
              HAPUS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
